import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, QueryList, Renderer2, ViewChild, ViewChildren } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Globals } from '../../globals';
import { CardFormHelper } from './card-form-helper/card-form.helper';
import { NoAccessModalComponent } from '../no-access-modal/no-access-modal.component';
import { AnalyticsService } from '../../services/analytics.service';

declare var Card: any;

class ShowOnDirtyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = !!form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-card-wrapper',
  templateUrl: './card-wrapper.component.html',
  styleUrls: ['./card-wrapper.component.scss']
})
export class CardWrapperComponent implements AfterViewInit {
  @ViewChild('cardContainer') cardContainer!: ElementRef;
  @ViewChildren('numberInput, expiryInput, cvcInput, nameInput') inputs!: QueryList<ElementRef>;
  @ViewChild('numberHiddenInput') numberHiddenInput!: ElementRef;
  @ViewChild('expiryHiddenInput') expiryHiddenInput!: ElementRef;

  @Output() formSubmitted = new EventEmitter<any>();

  cardForm!: FormGroup;
  showCvc = true;
  showOnDirtyErrorStateMatcher = new ShowOnDirtyErrorStateMatcher();
  tooltipInfo = "El código de seguridad o CVV (Card Verification Value) es\
    el número de tres dígitos ubicado en el dorso de la tarjeta o en la app\
    de tu banco o billetera digital.";
  tooltipTitle = "¿Qué es el CVV?";

  @Input() isLoading = false;
  CardFormHelper = CardFormHelper;
  private noAccessHandled = false;

  constructor(
    private fb: FormBuilder, 
    private renderer: Renderer2,
    private bottomSheet: MatBottomSheet,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    this.initializeCardForm();
  }

  ngAfterViewInit() {
    this.setCardJsDisplay();
    this.setupAutoFocus();
    this.setupBackspaceFocus();
    this.setupEmptyFieldClasses();
    this.setupExpiryHiddenSync();
  }

  private setupAutoFocus() {
    const controlNames = ['number', 'expiry', 'cvc'];
    controlNames.forEach(controlName => {
      this.cardForm.get(controlName)?.valueChanges.subscribe((value) => {
        const control = this.cardForm.get(controlName);
        if (CardFormHelper.isLastCharacterForInput(controlName, value)) {
          control?.markAsTouched();
          if (control?.valid) {
            this.focusPrevOrNextInput(controlName, "next");
          }
        }
      });
    });
  }

  /* On backspace, si el input se encuentra vacio, se hace focus al input anterior */
  private setupBackspaceFocus() {
    this.inputs.forEach((input: ElementRef) => {
      this.renderer.listen(input.nativeElement, 'keydown', (event: KeyboardEvent) => {
        if (event.key === 'Backspace') {
          this.handleBackspace(input.nativeElement.getAttribute('formControlName'));
        }
      });
    });
  }
  
  private handleBackspace(controlName: string) {
    const control = this.cardForm.get(controlName);
    if (control?.value === '') {
      this.focusPrevOrNextInput(controlName, "prev");
    }
  }

  handleEnterKeyOnCvc(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.focusPrevOrNextInput("cvc", "next");
  }

  handleEnterKeyOnName(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const input = event.target as HTMLElement | null;
    // En Android e iOS, un blur() dentro del keydown de Enter quita el foco pero el
    // teclado virtual, que aún procesa la tecla, queda abierto y sin input asociado.
    // Se difiere hasta que termine el evento para que el SO lo cierre.
    setTimeout(() => {
      input?.blur();
      if (this.cardForm.valid) {
        this.onSubmit();
      }
    });
  }

  private focusPrevOrNextInput(currentField: string, prevOrNext: "prev" | "next" = "next") {
    const inputsArray = this.inputs.toArray();
    const currentIndex = inputsArray.findIndex(input => input.nativeElement.getAttribute('formControlName') === currentField);
    
    const step = prevOrNext === "prev" ? -1 : 1;
    const newIndex = currentIndex + step;
    if (newIndex >= 0 && newIndex < inputsArray.length) {
      inputsArray[newIndex]?.nativeElement?.focus();
    }
  }

  private setupEmptyFieldClasses() {
    const container = this.cardContainer.nativeElement;
    const fields = ['number', 'expiry', 'cvc', 'name'];

    fields.forEach(field => {
      const className = `${field}-empty`;
      if (!this.cardForm.get(field)?.value) {
        this.renderer.addClass(container, className);
      }

      this.cardForm.get(field)?.valueChanges.subscribe(value => {
        if (value) {
          this.renderer.removeClass(container, className);
        } else {
          this.renderer.addClass(container, className);
        }
      });
    });
  }

  private initializeCardForm() {
    this.cardForm = this.fb.group({
      number: ['', [Validators.required, CardFormHelper.validateCardNumber, CardFormHelper.validateCardType]],
      name:   ['', [Validators.required, CardFormHelper.validateCardHolderName]],
      expiry: ['', [Validators.required, CardFormHelper.validateExpiryDate]],
      cvc:    ['', [Validators.required, Validators.pattern(/^[0-9]{3}$/)]]
    });
  }

  private setCardJsDisplay() {
    /* Cálculo del ancho de la tarjeta depende del ancho del viewport */
    const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    let cardWidth: number;
    if (viewportWidth > 1000) {
      cardWidth = Math.floor(viewportWidth * 0.3);
    } else if (viewportWidth > 540) {
      cardWidth = Math.floor(viewportWidth * 0.4);
    } else if (540 >= viewportWidth && viewportWidth > 318) {
      cardWidth = Math.floor(viewportWidth * 0.72);
    } else {
      cardWidth = Math.floor(viewportWidth * 0.68);
    }

    new Card({
      form: this.cardContainer.nativeElement.nextElementSibling,
      container: this.cardContainer.nativeElement,
      formSelectors: {
        numberInput: 'input#numberHidden',
        expiryInput: 'input#expiryHidden',
        cvcInput: 'input[formControlName="cvc"]',
        nameInput: 'input[formControlName="name"]'
      },
      messages: {
        validDate: 'fecha\nválida,',
        monthYear: '',
      },
      placeholders: {
        number: '•••• •••• •••• ••••',
        name: 'Nombre y apellido',
        expiry: 'MM/AA',
        cvc: ''
      },
      width: cardWidth
    });
  }

  toggleCvcMask(event: PointerEvent) {
    // Engloba eventos tipo "mouse", "touch" y "pen"
    event.preventDefault();
    this.showCvc = !this.showCvc;

    // Al ocultar el CVC, voltear la tarjeta al frente
    if (!this.showCvc) {
      const card = this.cardContainer.nativeElement.querySelector('.jp-card');
      card?.classList.remove('jp-card-flipped');
    }
    // Al mostrar el CVC, voltear la tarjeta al reverso
    if (this.showCvc) {
      const card = this.cardContainer.nativeElement.querySelector('.jp-card');
      card?.classList.add('jp-card-flipped');
    }
  }

  onCvcBlur() {
    this.showCvc = false;
    const card = this.cardContainer.nativeElement.querySelector('.jp-card');
    card?.classList.remove('jp-card-flipped');
  }
  
  onCvcFocus() {
    this.showCvc = true;
  }

  getMaskedCvc(): string {
    const value = this.cardForm.get('cvc')?.value || '';
    return '• '.repeat(value.length);
  }

  /* 
    Handles live formatting of the card number input, maintaining caret position and syncing with reactive form.
    - Formats digits as XXXX XXXX XXXX XXXX.
    - Updates both visible input and hidden form control without emitting events.
      ...(this is because we want to customize PAN masking)
    - Restores caret after formatting based on digits to the left of the caret.
  */
  restrictCardNumberInput(event: InputEvent | any) {
    const input = event.target as HTMLInputElement;

    // Capture caret before rewriting input.value
    const prevCaret = input.selectionStart ?? input.value.length;
    const prevValue = input.value;

    // Count how many digits were to the left of the caret in the previous value
    const digitsLeftOfCaret = (prevValue.slice(0, prevCaret).match(/\d/g) || []).length;

    // Clean and format
    const digits = prevValue.replace(/\D/g, '').slice(0, 16);
    const formatted = CardFormHelper.formatPanGroups(digits);

    // Check invalid bins which should be processed through TBK
    if (Globals.isUnsupportedBin(digits)) {
      this.handleNoAccess(digits);
      return;
    }

    // Update DOM + reactive form without emitting (avoid loops)
    input.value = formatted;
    this.cardForm.get('number')?.setValue(formatted, { emitEvent: false });

    // Keep card.js display in sync without letting it control the visible input
    const hidden = this.numberHiddenInput?.nativeElement as HTMLInputElement | undefined;
    if (hidden) {
      hidden.value = formatted;
      hidden.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
    }

    // Restore caret position based on digits count
    const newCaret = CardFormHelper.caretPosFromDigitsIndex(formatted, digitsLeftOfCaret);

    // Updates caret position
    input.setSelectionRange(newCaret, newCaret);

    // Manually trigger auto-focus on last character (iOS compatibility)
    if (CardFormHelper.isLastCharacterForInput('number', formatted)) {
      const control = this.cardForm.get('number');
      control?.markAsTouched();
      if (control?.valid) {
        this.focusPrevOrNextInput('number', 'next');
      }
    }
  }
  restrictExpiryInput(event: InputEvent|any) {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    let cleanedValue = currentValue.replace(/\D/g, '');

    if (event.inputType === 'deleteContentBackward') {
      // Si el valor actual es "MM / " y el usuario presiona backspace, borra " / "
      
      if ((currentValue.length === 5 && currentValue[4] === ' ') || (currentValue.length === 4)) {
        input.value = currentValue.slice(0, 2); // Keep only "MM"
        this.cardForm.get('expiry')?.setValue(input.value);
        event.preventDefault();
        return;
      }
      return;
    }

    // Maneja primer dígito si es entre [2-9]
    if (cleanedValue.length === 1 && /[2-9]/.test(cleanedValue)) {
      cleanedValue = `0${cleanedValue}`;
    }

    // Formatea como "MM / YY"
    if (cleanedValue.length > 2) {
      cleanedValue = `${cleanedValue.slice(0, 2)} / ${cleanedValue.slice(2, 4)}`;
    } else if (cleanedValue.length === 2) {
      cleanedValue = `${cleanedValue} / `;
    }

    // Actualiza valor del input
    input.value = cleanedValue;
    this.cardForm.get('expiry')?.setValue(cleanedValue);
  }

  /*
    Sincroniza el valor del expiry formControl con el hidden input
    para que card.js pueda mostrarlo en la gráfica
  */
  private setupExpiryHiddenSync() {
    this.cardForm.get('expiry')?.valueChanges.subscribe(value => {
      const hidden = this.expiryHiddenInput?.nativeElement as HTMLInputElement | undefined;
      if (hidden) {
        hidden.value = value;
        hidden.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
      }
      // card.js filtra el input value "0" para mostrarlo en la gráfica — manual override
      if (value === '0') {
        const expiryEl = this.cardContainer.nativeElement.querySelector('.jp-card-expiry');
        if (expiryEl) {
          expiryEl.textContent = '0';
        }
      }
    });
  }

  private handleNoAccess(bin: string) {
    if (this.noAccessHandled) return;
    this.noAccessHandled = true;

    this.bottomSheet
      .open(NoAccessModalComponent, {
        disableClose: true,
        panelClass: 'no-access-bottom-sheet',
        backdropClass: 'no-access-bottom-sheet-backdrop'
      })
      .afterDismissed()
      .subscribe(() => {
        this.analyticsService.derivarOneclickTokenizacion(bin);
        Globals.manuallyNavigateTo('/redirecttbk');
      });
  }

  onSubmit() {
    if (!this.cardForm.valid || this.isLoading) return; // Impide submit on Enter/Intro key
    (document.activeElement as HTMLElement)?.blur(); // Ocultar teclado virtual
    const expiryParts = this.cardForm.get('expiry')?.value.split(' / ');

    const cleanedForm = {
      number: this.cardForm.get('number')?.value.replace(/\s+/g, ''), // Remove spaces
      expiry_month: expiryParts[0],
      expiry_year: `20${expiryParts[1]}`,
      cvc: this.cardForm.get('cvc')?.value,
    };

    this.formSubmitted.emit(cleanedForm);
  }

  getCardNumberErrorMessage(): string {
    if (this.cardForm.get('number')?.hasError('required')) {
      return "El número de la tarjeta es requerido.";
    }
    if (this.cardForm.get('number')?.hasError('invalidCardType')) {
      return "Solo aceptamos VISA o MASTERCARD.";
    } 
    if (this.cardForm.get('number')?.hasError('invalidCardNumber')) {
      return "El número de la tarjeta no es válido.";
    } 
    return "El número de la tarjeta no es valido.";
  }
  getCardHolderNameErrorMessage(): string {
    if (this.cardForm.get('name')?.hasError('required')) {
      return "El nombre del titular es requerido.";
    }
    if (this.cardForm.get('name')?.hasError('invalidCardHolderName')) {
      return "El nombre del titular no es válido.";
    }
    return "El nombre del titular no es válido.";
  }
  getExpiryDateErrorMessage(): string {
    if (this.cardForm.get('expiry')?.hasError('required')) {
      return "La fecha es requerida.";
    }
    if (this.cardForm.get('expiry')?.hasError('invalidMonth')) {
      return "Mes no válido.";
    }
    if (this.cardForm.get('expiry')?.hasError('invalidYear')) {
      return "Año no válido.";
    }
    if (this.cardForm.get('expiry')?.hasError('expired')) {
      return "Tarjeta caducada.";
    }
    if (this.cardForm.get('expiry')?.hasError('invalidDate')) {
      return "Fecha no válida.";
    }
    return "Fecha no válida.";
  }

}
